/**
 * The Attendance page's Submit button and its open-time order, as plain
 * functions with no Firebase in them so a node test can import this file.
 *
 * Bobbie, 2026-10-18, verbatim: "we need a submit button under attendance"
 * and "it would be really great after you take attendance that if there's
 * anybody missing that they go to the top so like as you clear them off,
 * anybody who is still left shows up at the top the next time you go back
 * in the moment, keep them so that you can go down the list, but if you go
 * back to the page to edit it, it shows anybody who's missing like you did
 * didn't record anything." Then, same day: "Submit button Button is a big
 * one for attendance as well".
 *
 * Marks still save on every tap. Submit is the teacher's "I'm done": it
 * names anybody still unmarked, and when nobody is it closes the Take
 * attendance to-do on the spot (netlify/functions/attendanceSubmit.mjs)
 * instead of leaving it for the hourly job.
 *
 * Imports nothing, so netlify/functions/attendanceSubmit.mjs can load it by
 * its .ts path.
 */

/** One roster row as these functions read it. Structural, so StudentDoc fits. */
export interface AttendanceRosterRow {
  id: string;
  firstName: string;
  lastName: string;
}

/** Ids of roster dancers with no mark yet, in roster order. */
export function unmarkedRosterIds(
  rosterIds: readonly string[],
  markedIds: ReadonlySet<string> | { has(id: string): boolean }
): string[] {
  return rosterIds.filter((id) => !markedIds.has(id));
}

/**
 * THE ORDER IS TAKEN ONCE, WHEN THE PAGE OPENS ON A CLASS AND DAY.
 *
 * Dancers with no mark go first, then everyone else, each group keeping the
 * order it already had (last name, first name). The page stores the answer
 * and does NOT ask again on every tap, because a row that jumped to the
 * bottom the moment she tapped it would put the next dancer under her thumb
 * by surprise. Coming back to the page, or picking another class or day,
 * asks again, which is when the stragglers should be on top.
 */
export function openTimeRosterOrder(
  rosterIds: readonly string[],
  markedIds: ReadonlySet<string> | { has(id: string): boolean }
): string[] {
  const unmarked: string[] = [];
  const marked: string[] = [];
  for (const id of rosterIds) (markedIds.has(id) ? marked : unmarked).push(id);
  return [...unmarked, ...marked];
}

/**
 * Lay today's roster out in the order taken at open. A dancer added to the
 * roster since (not in the stored order) goes to the end rather than
 * vanishing, and a dancer who left is simply not drawn.
 */
export function applyRosterOrder<T extends { id: string }>(
  roster: readonly T[],
  order: readonly string[] | null
): T[] {
  if (!order) return [...roster];
  const rank = new Map(order.map((id, index) => [id, index]));
  return roster
    .map((row, index) => ({ row, index }))
    .sort((a, b) => {
      const ra = rank.get(a.row.id);
      const rb = rank.get(b.row.id);
      if (ra !== undefined && rb !== undefined) return ra - rb;
      if (ra !== undefined) return -1;
      if (rb !== undefined) return 1;
      return a.index - b.index;
    })
    .map(({ row }) => row);
}

/** The full name, the way the roster row itself reads, so the two match. */
export function submitName(row: AttendanceRosterRow): string {
  const name = `${(row.firstName ?? '').trim()} ${(row.lastName ?? '').trim()}`.trim();
  return name || 'A dancer';
}

export type SubmitCheck =
  | { kind: 'empty' }
  | { kind: 'missing'; missing: AttendanceRosterRow[]; message: string }
  | { kind: 'ready' };

/**
 * What pressing Submit says before anything is sent. Missing dancers are
 * named, in the order they sit on the page, so she can tap straight to each.
 */
export function submitAttendanceCheck(
  roster: readonly AttendanceRosterRow[],
  markedIds: ReadonlySet<string> | { has(id: string): boolean }
): SubmitCheck {
  if (roster.length === 0) return { kind: 'empty' };
  const missing = roster.filter((row) => !markedIds.has(row.id));
  if (missing.length === 0) return { kind: 'ready' };
  const message = missing.length === 1
    ? `1 dancer still needs a mark: ${submitName(missing[0])}. Tap the name to jump to that row.`
    : `${missing.length} dancers still need a mark. Tap a name to jump to that row.`;
  return { kind: 'missing', missing, message };
}

/** The confirmation once the server has the whole register. */
export function submittedMessage(className: string, dateLabel: string): string {
  return `Attendance for ${className} on ${dateLabel} is submitted. The reminder is cleared.`;
}

/** The record-only cancel's confirmation. Marks are never touched by it. */
export function cancelledRecordedMessage(className: string, dateLabel: string): string {
  return `${className} on ${dateLabel} is recorded as cancelled. No families were notified and the reminder is cleared.`;
}

/**
 * "Tuesday, October 13" for a studio day key, spelled out in full because the
 * confirm step is the one place a wrong day must be impossible to miss.
 * Read at noon UTC so no device timezone can move it to a neighbouring day.
 */
export function longDateLabel(dateKey: string): string {
  const [y, m, d] = dateKey.split('-').map(Number);
  if (!y || !m || !d) return dateKey;
  return new Date(Date.UTC(y, m - 1, d, 12)).toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric', timeZone: 'UTC',
  });
}

/**
 * The confirm step's question for the record-only cancel. Two taps, always
 * (Bobbie, 2026-10-18: "Canceling class should make you click it and then
 * are you sure this class was canceled so that it's not accidentally
 * clicked. And it should show the date that they are canceling it on."),
 * and it names the class and the day in words.
 */
export function cancelConfirmQuestion(className: string, dateKey: string): string {
  return `Was ${className} cancelled on ${longDateLabel(dateKey)}? This clears the attendance reminder for that day. Marks already taken are kept but not counted, and Undo brings them back. Nobody is notified.`;
}

/** The cancelled-day banner, under its heading. */
export function cancelledDayExplainer(): string {
  return 'There is no attendance to take for this day, so the reminder is cleared and nobody is counted absent. Any marks already taken are kept, and they count again if the cancel is undone.';
}

/** After Undo. */
export function reinstatedMessage(
  className: string,
  dateKey: string,
  result: { bookingRemoved: boolean; familiesWereTold: boolean }
): string {
  const parts = [`${className} on ${longDateLabel(dateKey)} is back on. Its attendance counts again.`];
  if (result.bookingRemoved) {
    parts.push('The room booking was taken off the calendar when it was cancelled. Book it again from the Calendar if you need it.');
  }
  if (result.familiesWereTold) {
    parts.push('Families were told it was cancelled, so post on the class board to let them know it is back on.');
  }
  return parts.join(' ');
}

/** A YYYY-MM-DD that names a real calendar day (no 2026-02-31, no month 13). */
export function isRealDateKey(key: string): boolean {
  if (typeof key !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(key)) return false;
  const [y, m, d] = key.split('-').map(Number);
  const probe = new Date(Date.UTC(y, m - 1, d));
  return probe.getUTCFullYear() === y && probe.getUTCMonth() === m - 1 && probe.getUTCDate() === d;
}

/**
 * THE AUTO-CLEAR, DECIDED. The page asks the server to close the reminder
 * once it sees every dancer marked. A check that raced the last mark to the
 * server would come back "not yet" and the reminder would stay, so: wait
 * while the marks are unconfirmed or a write is still on its way, ask once
 * they are settled, stop for good only when the server said complete, and
 * give up after `maxAttempts` "not yet" answers so a page left open cannot
 * call the server for ever.
 */
export type AutoSubmitStep = 'wait' | 'ask' | 'stop';

export function autoSubmitStep(input: {
  canSubmit: boolean;
  isFutureDay: boolean;
  allMarked: boolean;
  marksConfirmed: boolean;
  marksPending: boolean;
  /** The server already answered complete for this class and day. */
  confirmedComplete: boolean;
  /** "Not yet" answers so far for this class and day. */
  attempts: number;
  /** A request for this class and day is already out. */
  inFlight: boolean;
  maxAttempts?: number;
}): AutoSubmitStep {
  const max = input.maxAttempts ?? 3;
  if (!input.canSubmit || input.isFutureDay || input.confirmedComplete || input.attempts >= max) return 'stop';
  if (!input.allMarked || !input.marksConfirmed || input.marksPending || input.inFlight) return 'wait';
  return 'ask';
}

/** The shape attendanceSubmit.mjs answers with. */
export interface AttendanceSubmitResult {
  ok: true;
  complete: boolean;
  cancelled: boolean;
  unmarkedStudentIds: string[];
  nagClosed: boolean;
}
